
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { articleService } from '../services/api';
import { useTheme, useColor, useBrand } from '../context/ColorContext';
import { Maximize2, Heart, Share2, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Ilham = () => {
    const { theme } = useTheme();
    const { brandData } = useColor();
    const { activeBrand } = useBrand();
    const isDark = theme === 'dark';

    const mascotImages = {
        'dufa': '/img/dufa-kirpi-transparan.png',
        'genc': '/img/genc-maskot.png',
        'woodsol': '/img/woodsol-maskot.png'
    };

    const navigate = useNavigate();

    const { data: articles, isLoading } = useQuery({
        queryKey: ['articles', activeBrand.id],
        queryFn: () => articleService.getAll(activeBrand.id)
    });

    return (
        <div className={`min-h-screen pt-32 pb-20 px-4 md:px-0 container mx-auto ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {/* Header Area */}
            <div className="text-center mb-20">
                <span className="text-sm font-bold tracking-[0.4em] opacity-60 uppercase">Hayallerinizi Boyayın</span>
                <h1 className="text-5xl md:text-8xl font-black uppercase mt-4 mb-6 tracking-tighter leading-none">İlham Alın</h1>
                <p className="max-w-2xl mx-auto opacity-70 text-lg font-light leading-relaxed">
                    {activeBrand.name} renkleriyle tasarlanmış gerçek mekanlardan ilham alın. Her köşe bir hikaye, her renk bir duygu barındırır.
                </p>
            </div>

            {/* Content Area */}
            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <Loader2 className="animate-spin text-slate-400" size={48} />
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Yükleniyor...</p>
                </div>
            ) : (
                <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
                    {articles?.map((item) => (
                        <div
                            key={item.id}
                            onClick={() => navigate(`/${activeBrand.id}/ilham/${item.slug}`)}
                            className="break-inside-avoid relative group rounded-[2rem] overflow-hidden cursor-pointer shadow-2xl transition-all duration-500 hover:-translate-y-2"
                        >
                            {/* Image */}
                            <img
                                src={item.image || '/img/placeholder.png'}
                                alt={item.title}
                                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110 min-h-[250px] bg-slate-100 dark:bg-slate-800"
                            />

                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 backdrop-blur-[2px] flex flex-col justify-between p-8">
                                <div className="flex justify-end gap-3 text-white">
                                    <button className="p-3 bg-white/10 hover:bg-white/20 rounded-full backdrop-blur-md transition-colors" onClick={(e) => e.stopPropagation()}>
                                        <Heart size={20} />
                                    </button>
                                    <button className="p-3 bg-white/10 hover:bg-white/20 rounded-full backdrop-blur-md transition-colors" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(window.location.origin + `/${activeBrand.id}/ilham/${item.slug}`); }}>
                                        <Share2 size={20} />
                                    </button>
                                </div>

                                <div>
                                    <div className="flex items-center gap-3 mb-3">
                                        <div
                                            className="w-4 h-4 rounded-full border border-white/50"
                                            style={{ backgroundColor: item.color || '#fff' }}
                                        />
                                        <span className="text-xs font-bold uppercase tracking-widest text-white/80">
                                            İlham Rengi
                                        </span>
                                    </div>
                                    <h3 className="text-2xl font-black text-white uppercase mb-4">
                                        {item.title}
                                    </h3>
                                    <button
                                        className="flex items-center gap-2 text-white font-bold text-xs uppercase tracking-[0.2em] px-6 py-3 rounded-full transition-colors"
                                        style={{ backgroundColor: activeBrand.primaryColor }}
                                    >
                                        Detayları Gör <Maximize2 size={16} />
                                    </button>
                                </div>
                            </div>

                            {/* Static Info Label */}
                            {item.tag && (
                                <div className="absolute top-6 left-6 group-hover:opacity-0 transition-opacity">
                                    <span className="px-5 py-2 rounded-full bg-white/80 backdrop-blur-md text-[10px] font-black uppercase text-slate-800 tracking-widest shadow-lg">
                                        {item.tag}
                                    </span>
                                </div>
                            )}
                        </div>
                    ))}
                    {articles?.length === 0 && (
                        <div className="col-span-full py-20 text-center opacity-50">
                            <p className="text-lg font-bold">Henüz içerik bulunmamaktadır.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Mascot Section */}
            <div className={`mt-32 p-16 rounded-[4rem] relative overflow-hidden flex flex-col items-center text-center ${isDark ? 'bg-white/5 border border-white/10' : 'bg-black/5 border border-black/5'}`}>
                <div className="relative z-10 max-w-2xl">
                    <h2 className="text-4xl md:text-6xl font-black uppercase mb-6 leading-tight tracking-tighter">Kendi Tarzınızı <br /> Yansıtın</h2>
                    <p className="opacity-70 text-lg mb-10 leading-relaxed">Renklerimizin mekanlardaki duruşunu daha yakından incelemek için numune talebinde bulunabilirsiniz.</p>
                    <div className="flex flex-col md:flex-row gap-4 justify-center">
                        <button
                            className="px-10 py-5 text-white rounded-2xl font-black uppercase tracking-widest shadow-2xl hover:scale-105 transition-transform"
                            style={{ backgroundColor: activeBrand.primaryColor }}
                        >
                            Numune Talebi
                        </button>
                        <button className={`px-10 py-5 rounded-2xl font-black uppercase tracking-widest border transition-all ${isDark ? 'border-white/20 hover:bg-white/10' : 'border-black/10 hover:bg-black/5'}`}>
                            Katalog İndir
                        </button>
                    </div>
                </div>

                {/* Floating Mascot */}
                <div className="absolute bottom-6 -right-10 w-64 h-64 md:w-96 md:h-96 opacity-30 pointer-events-none animate-bounce-subtle z-[60]">
                    <img
                        src={mascotImages[activeBrand.id] || mascotImages['dufa']}
                        alt={activeBrand.name}
                        className="w-full h-full object-contain grayscale brightness-150"
                        onError={(e) => { e.target.src = mascotImages['dufa']; }}
                    />
                </div>
            </div>
        </div>
    );
};

export default Ilham;
